import { getAuth } from "firebase/auth";
import { collection, doc, getDoc, getDocs, getFirestore, increment, onSnapshot, orderBy, query, updateDoc } from "firebase/firestore";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiChevronDown, FiFilter, FiHeart, FiMusic, FiPause, FiPlay, FiSearch, FiShoppingBag, FiSliders, FiStar, FiUserPlus, FiX } from "react-icons/fi";
import { Link, useSearchParams } from "react-router-dom";
import Checkout from "../Components/Checkout";
import FollowButton from "../Components/FollowButton";
import { app } from "../firebase";
import { ANALYTICS_EVENTS, buildBeatAnalyticsParams, trackAnalyticsEvent } from "../services/analytics";
import { isLiveTrack, normalizeTrackDocument } from "../utils/tracks";
import "./Marketplace.css";

const db = getFirestore(app);
const auth = getAuth(app);

const getTrackGenres = (track) => Array.isArray(track.genres) && track.genres.length
    ? track.genres
    : [track.genre || "Uncategorized"];

const formatPrice = (price) => {
    const numericPrice = Number(price || 0);
    return Number.isFinite(numericPrice) ? `$${numericPrice.toFixed(2)}` : "$0.00";
};

const priceValue = (price) => {
    const numericPrice = Number(price || 0);
    return Number.isFinite(numericPrice) ? numericPrice : 0;
};

const dateValue = (value) => {
    if (!value) return 0;
    if (typeof value.toMillis === "function") return value.toMillis();
    if (typeof value.toDate === "function") return value.toDate().getTime();
    const parsed = new Date(value).getTime();
    return Number.isNaN(parsed) ? 0 : parsed;
};

const formatTime = (seconds) => {
    if (!Number.isFinite(seconds) || seconds <= 0) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60).toString().padStart(2, "0");
    return `${mins}:${secs}`;
};

const normalizeTrack = normalizeTrackDocument;

const sortLabels = {
    newest: "Newest",
    popular: "Most popular",
    "price-low": "Price low",
    "price-high": "Price high",
    title: "Title A-Z",
    oldest: "Oldest",
};

const isValidTrack = isLiveTrack;

const getProfileDisplayName = (profileData = {}) => (
    profileData.profile?.name ||
    profileData.displayName ||
    profileData.username ||
    ""
);

const TrackCard = ({
    track,
    isFeatured = false,
    isPlaying,
    isWishlisted,
    currentUserFollowing,
    onFollowChange,
    onPlay,
    onWishlist,
    onCheckout,
}) => {
    const profilePath = track.userId ? `/profile/${track.userId}` : "#";
    const genres = getTrackGenres(track);

    return (
        <article className={`market-track-card ${isFeatured ? "featured" : ""}`}>
            <div className="market-card-media">
                <img src={track.coverImage} alt={track.title || "Beat artwork"} />
                <button
                    type="button"
                    className={`market-play-button ${track.fileUrl ? "" : "disabled"}`}
                    onClick={(event) => onPlay(track, event, isFeatured ? "featured_grid" : "catalog_grid")}
                    disabled={!track.fileUrl}
                    aria-label={track.fileUrl ? (isPlaying ? `Pause ${track.title}` : `Play ${track.title}`) : "Preview unavailable"}
                >
                    {isPlaying ? <FiPause /> : <FiPlay />}
                </button>
                <button
                    type="button"
                    className={`market-save-button ${isWishlisted ? "active" : ""}`}
                    onClick={(event) => {
                        event.stopPropagation();
                        onWishlist(track);
                    }}
                    aria-label={isWishlisted ? "Remove from saved beats" : "Save beat"}
                >
                    <FiHeart />
                </button>
                {isFeatured && (
                    <span className="market-card-badge">
                        <FiStar />
                        Featured
                    </span>
                )}
            </div>

            <div className="market-card-body">
                <div className="market-card-title-row">
                    <div>
                        <h3>{track.title || "Untitled beat"}</h3>
                        <Link to={profilePath} className="market-artist-link">
                            {track.artist || "Unknown producer"}
                        </Link>
                    </div>
                    <strong>{formatPrice(track.price)}</strong>
                </div>

                <div className="market-card-meta">
                    <span>{genres[0]}</span>
                    {track.bpm && <span>{track.bpm} BPM</span>}
                    {track.key && <span>{track.key}</span>}
                </div>

                <div className="market-card-license">
                    <span>MP3 + WAV</span>
                    <span>Instant license</span>
                    <span>No buyer fees</span>
                </div>

                <div className="market-card-actions">
                    {track.userId ? (
                        <FollowButton
                            targetUserId={track.userId}
                            targetUserName={track.artist}
                            currentUserFollowing={currentUserFollowing}
                            onFollowChange={onFollowChange}
                        />
                    ) : (
                        <span className="market-follow-placeholder">
                            <FiUserPlus />
                            Producer
                        </span>
                    )}
                    <button
                        type="button"
                        className="market-license-button"
                        onClick={() => onCheckout(track)}
                        title="License this beat"
                    >
                        <FiShoppingBag />
                        License
                    </button>
                </div>
            </div>
        </article>
    );
};

const Marketplace = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [tracks, setTracks] = useState([]);
    const [search, setSearch] = useState(searchParams.get("search") || "");
    const [selectedGenre, setSelectedGenre] = useState("All");
    const [sortBy, setSortBy] = useState("newest");
    const [priceRange, setPriceRange] = useState({ min: 0, max: 1000 });
    const [wishlist, setWishlist] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [currentUserFollowing, setCurrentUserFollowing] = useState([]);
    const [checkoutTrack, setCheckoutTrack] = useState(null);
    const [checkoutSource, setCheckoutSource] = useState("marketplace");
    const [playingTrack, setPlayingTrack] = useState(null);
    const [showPriceFilter, setShowPriceFilter] = useState(false);
    const [recentlyViewed, setRecentlyViewed] = useState([]);
    const [playerTime, setPlayerTime] = useState(0);
    const [playerDuration, setPlayerDuration] = useState(0);
    const audioRef = useRef(null);
    const selectedArtist = searchParams.get("artist") || "";

    const genres = ["All", "Hip-Hop", "Trap", "Drill", "R&B", "Pop", "Electronic", "Afrobeat", "Lo-Fi"];

    useEffect(() => {
        const viewed = localStorage.getItem("recentlyViewed");
        if (viewed) {
            try {
                setRecentlyViewed(JSON.parse(viewed));
            } catch {
                setRecentlyViewed([]);
            }
        }
    }, []);

    useEffect(() => {
        setSearch(searchParams.get("search") || "");
    }, [searchParams]);

    const hydrateTrackArtistNames = useCallback(async (trackList = []) => {
        const uniqueUserIds = [...new Set(trackList.map((track) => track.userId).filter(Boolean))];
        if (uniqueUserIds.length === 0) return trackList;

        try {
            const profileEntries = await Promise.all(uniqueUserIds.map(async (userId) => {
                const profileSnap = await getDoc(doc(db, "profiles", userId));
                const displayName = profileSnap.exists() ? getProfileDisplayName(profileSnap.data()) : "";
                return [userId, displayName];
            }));
            const displayNameByUserId = Object.fromEntries(profileEntries.filter(([, displayName]) => displayName));

            return trackList.map((track) => ({
                ...track,
                artist: displayNameByUserId[track.userId] || track.artist,
            }));
        } catch (err) {
            console.warn("Could not refresh producer names for marketplace cards:", err);
            return trackList;
        }
    }, []);

    const fetchTracks = useCallback(async () => {
        setLoading(true);
        try {
            let querySnapshot;
            try {
                querySnapshot = await getDocs(query(collection(db, "tracks"), orderBy("uploadedAt", "desc")));
            } catch (orderedError) {
                console.warn("Ordered marketplace query failed, loading unsorted tracks:", orderedError);
                querySnapshot = await getDocs(collection(db, "tracks"));
            }
            const fetchedTracks = querySnapshot.docs.map(normalizeTrack).filter(isValidTrack);
            setTracks(await hydrateTrackArtistNames(fetchedTracks));
            setError("");
        } catch (err) {
            console.error("Error fetching tracks:", err);
            setError("Failed to load beats.");
        } finally {
            setLoading(false);
        }
    }, [hydrateTrackArtistNames]);

    const fetchCurrentUserFollowing = useCallback(async () => {
        const user = auth?.currentUser;
        if (!user) return;

        try {
            const profileDoc = await getDoc(doc(db, "profiles", user.uid));
            if (profileDoc.exists()) {
                const following = profileDoc.data().following || [];
                setCurrentUserFollowing(Array.isArray(following) ? following : []);
            }
        } catch (err) {
            console.error("Error fetching following list:", err);
        }
    }, []);

    const loadWishlist = useCallback(() => {
        const saved = localStorage.getItem("wishlist");
        if (saved) {
            try {
                setWishlist(JSON.parse(saved));
            } catch {
                setWishlist([]);
            }
        }
    }, []);

    useEffect(() => {
        const tracksQuery = query(collection(db, "tracks"), orderBy("uploadedAt", "desc"));
        const unsubscribe = onSnapshot(
            tracksQuery,
            async (snapshot) => {
                const fetchedTracks = snapshot.docs.map(normalizeTrack).filter(isValidTrack);
                const hydratedTracks = await hydrateTrackArtistNames(fetchedTracks);
                setTracks(hydratedTracks);
                setLoading(false);
            },
            (err) => {
                console.error("Error listening to tracks:", err);
                setError("Failed to load live beats. Retrying with a standard Firestore read.");
                fetchTracks();
            }
        );

        fetchCurrentUserFollowing();
        loadWishlist();

        return () => {
            if (typeof unsubscribe === "function") {
                unsubscribe();
            }
        };
    }, [fetchCurrentUserFollowing, fetchTracks, hydrateTrackArtistNames, loadWishlist]);

    const trackView = async (trackId) => {
        if (!trackId) return;
        try {
            await updateDoc(doc(db, "tracks", trackId), {
                viewCount: increment(1),
            });
        } catch (err) {
            console.error("Error tracking view:", err);
        }
    };

    const addToRecentlyViewed = (track) => {
        const viewed = recentlyViewed.filter((item) => item.id !== track.id);
        const updated = [track, ...viewed].slice(0, 10);
        setRecentlyViewed(updated);
        localStorage.setItem("recentlyViewed", JSON.stringify(updated));
    };

    const saveWishlist = (newWishlist) => {
        localStorage.setItem("wishlist", JSON.stringify(newWishlist));
        setWishlist(newWishlist);
    };

    const handleAddToWishlist = (track) => {
        const isWishlisted = wishlist.some((item) => item.id === track.id);
        saveWishlist(isWishlisted ? wishlist.filter((item) => item.id !== track.id) : [...wishlist, track]);
    };

    const handlePlayPreview = (track, event, source = "marketplace") => {
        event?.stopPropagation();

        if (playingTrack?.id === track.id) {
            audioRef.current?.pause();
            setPlayingTrack(null);
            return;
        }

        setPlayerTime(0);
        setPlayerDuration(0);
        setPlayingTrack(track);
        trackView(track.id);
        addToRecentlyViewed(track);
        trackAnalyticsEvent(ANALYTICS_EVENTS.BEAT_PREVIEW, buildBeatAnalyticsParams(track, {
            source,
        }));
    };

    const handleAudioTimeUpdate = (event) => {
        const audio = event.target;
        setPlayerTime(audio.currentTime || 0);
        setPlayerDuration(audio.duration || 0);

        if (audio.currentTime >= 30) {
            audio.pause();
            audio.currentTime = 0;
            setPlayingTrack(null);
            setPlayerTime(0);
        }
    };

    const clearArtistFilter = () => {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete("artist");
        setSearchParams(nextParams);
    };

    const handleSearchChange = (value) => {
        setSearch(value);
        const nextParams = new URLSearchParams(searchParams);
        if (value.trim()) {
            nextParams.set("search", value);
        } else {
            nextParams.delete("search");
        }
        setSearchParams(nextParams, { replace: true });
    };

    const handleCheckout = (track, source = "marketplace") => {
        if (!auth.currentUser) {
            alert("Please log in to purchase tracks.");
            return;
        }
        if (track.userId === auth.currentUser.uid) {
            alert("You cannot purchase your own track.");
            return;
        }
        setCheckoutSource(source);
        setCheckoutTrack(track);
    };

    const handleFollowChange = (userId, isFollowing) => {
        if (isFollowing) {
            setCurrentUserFollowing((prev) => [...prev, userId]);
        } else {
            setCurrentUserFollowing((prev) => prev.filter((id) => id !== userId));
        }
    };

    const handleSeek = (event) => {
        const nextTime = Number(event.target.value);
        if (audioRef.current) {
            audioRef.current.currentTime = nextTime;
        }
        setPlayerTime(nextTime);
    };

    useEffect(() => {
        if (playingTrack?.fileUrl && audioRef.current) {
            audioRef.current.play().catch((err) => {
                console.warn("Preview playback did not start automatically:", err);
            });
        }
    }, [playingTrack]);

    useEffect(() => {
        const audioNode = audioRef.current;
        return () => {
            audioNode?.pause();
        };
    }, []);

    const filteredTracks = useMemo(() => tracks
        .filter((track) => {
            const trackGenres = getTrackGenres(track);
            const queryText = search.toLowerCase();
            const matchesSearch =
                (track.title || "").toLowerCase().includes(queryText) ||
                (track.artist || "").toLowerCase().includes(queryText) ||
                trackGenres.some((genre) => genre.toLowerCase().includes(queryText));
            const matchesArtist = !selectedArtist || (track.artist || "").toLowerCase() === selectedArtist.toLowerCase();
            const matchesGenre = selectedGenre === "All" || trackGenres.includes(selectedGenre);
            const matchesPrice = priceValue(track.price) >= priceRange.min && priceValue(track.price) <= priceRange.max;
            return matchesSearch && matchesArtist && matchesGenre && matchesPrice;
        })
        .sort((a, b) => {
            switch (sortBy) {
                case "oldest":
                    return dateValue(a.uploadedAt) - dateValue(b.uploadedAt);
                case "price-low":
                    return priceValue(a.price) - priceValue(b.price);
                case "price-high":
                    return priceValue(b.price) - priceValue(a.price);
                case "title":
                    return (a.title || "").localeCompare(b.title || "");
                case "popular":
                    return (b.salesCount || b.viewCount || 0) - (a.salesCount || a.viewCount || 0);
                case "newest":
                default:
                    return dateValue(b.uploadedAt) - dateValue(a.uploadedAt);
            }
        }), [priceRange.max, priceRange.min, search, selectedArtist, selectedGenre, sortBy, tracks]);

    useEffect(() => {
        if (loading || error) return;

        trackAnalyticsEvent(ANALYTICS_EVENTS.MARKETPLACE_VIEWED, {
            source: selectedArtist ? "artist_filter" : "marketplace",
            result_count: filteredTracks.length,
            total_count: tracks.length,
            genre: selectedGenre,
            sort_by: sortBy,
            min_price: priceRange.min,
            max_price: priceRange.max,
            has_search: Boolean(search.trim()),
        });
    }, [error, filteredTracks.length, loading, priceRange.max, priceRange.min, search, selectedArtist, selectedGenre, sortBy, tracks.length]);

    useEffect(() => {
        const queryText = search.trim();
        if (loading || error || !queryText) return undefined;

        const timeout = window.setTimeout(() => {
            trackAnalyticsEvent(ANALYTICS_EVENTS.MARKETPLACE_SEARCH, {
                source: "marketplace",
                query_length: queryText.length,
                result_count: filteredTracks.length,
                genre: selectedGenre,
                sort_by: sortBy,
            });
        }, 700);

        return () => window.clearTimeout(timeout);
    }, [error, filteredTracks.length, loading, search, selectedGenre, sortBy]);

    const featuredTracks = useMemo(() => {
        const topSellers = tracks.filter((track) => track.isTopSeller).slice(0, 4);
        if (topSellers.length >= 4) return topSellers;

        const newest = tracks
            .filter((track) => !topSellers.some((featured) => featured.id === track.id))
            .sort((a, b) => dateValue(b.uploadedAt) - dateValue(a.uploadedAt))
            .slice(0, 4 - topSellers.length);

        return [...topSellers, ...newest];
    }, [tracks]);

    const marketStats = [
        { label: "Live beats", value: tracks.length.toLocaleString() },
        { label: "Featured picks", value: featuredTracks.length.toLocaleString() },
        { label: "Saved", value: wishlist.length.toLocaleString() },
    ];

    const renderCard = (track, isFeatured = false) => (
        <TrackCard
            key={track.id}
            track={track}
            isFeatured={isFeatured}
            isPlaying={playingTrack?.id === track.id}
            isWishlisted={wishlist.some((item) => item.id === track.id)}
            currentUserFollowing={currentUserFollowing}
            onFollowChange={handleFollowChange}
            onPlay={handlePlayPreview}
            onWishlist={handleAddToWishlist}
            onCheckout={handleCheckout}
        />
    );

    return (
        <div className={`marketplace-page ${playingTrack ? "has-player" : ""}`}>
            <header className="market-hero">
                <div className="market-shell">
                    <div className="market-hero-grid">
                        <div className="market-hero-copy">
                            <span className="market-eyebrow">Tune marketplace</span>
                            <h1>Find polished beats without fighting the marketplace.</h1>
                            <p>
                                Preview fast, compare the license, save your shortlist, and check out with a clean buyer flow.
                            </p>
                            <div className="market-search-panel">
                                <FiSearch />
                                <input
                                    type="text"
                                    placeholder="Search trap drums, R&B loops, producer names..."
                                    value={search}
                                    onChange={(event) => handleSearchChange(event.target.value)}
                                    aria-label="Search beats, artists, or genres"
                                />
                                {search && (
                                    <button type="button" onClick={() => handleSearchChange("")} aria-label="Clear search">
                                        <FiX />
                                    </button>
                                )}
                            </div>
                            <div className="market-trust-strip" aria-label="Marketplace promises">
                                <span>30 sec previews</span>
                                <span>MP3 + WAV licenses</span>
                                <span>No added buyer fees</span>
                            </div>
                        </div>

                        <aside className="market-hero-card" aria-label="Marketplace snapshot">
                            <div className="market-hero-card-top">
                                <span>Now browsing</span>
                                <strong>{filteredTracks.length.toLocaleString()} beats</strong>
                            </div>
                            <div className="market-mini-stack">
                                {featuredTracks.slice(0, 3).map((track, index) => (
                                    <button
                                        type="button"
                                        key={track.id}
                                        className="market-mini-row"
                                        disabled={!track.fileUrl}
                                        onClick={(event) => handlePlayPreview(track, event, "featured_mini")}
                                    >
                                        <img src={track.coverImage} alt="" />
                                        <span>
                                            <strong>{track.title || "Untitled beat"}</strong>
                                            <small>{track.artist || "Unknown producer"}</small>
                                        </span>
                                        <em>#{index + 1}</em>
                                    </button>
                                ))}
                            </div>
                        </aside>
                    </div>

                    <div className="market-stats-row" aria-label="Marketplace stats">
                        {marketStats.map((stat) => (
                            <div key={stat.label}>
                                <strong>{stat.value}</strong>
                                <span>{stat.label}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </header>

            <main className="market-shell">
                <section className="market-toolbar" aria-label="Marketplace filters">
                    <div className="market-filter-group">
                        {genres.map((genre) => (
                            <button
                                key={genre}
                                type="button"
                                className={selectedGenre === genre ? "active" : ""}
                                onClick={() => setSelectedGenre(genre)}
                            >
                                {genre}
                            </button>
                        ))}
                    </div>

                    <div className="market-toolbar-actions">
                        <label className="market-select">
                            <span>Sort</span>
                            <strong>{sortLabels[sortBy]}</strong>
                            <select value={sortBy} onChange={(event) => setSortBy(event.target.value)}>
                                <option value="newest">Newest</option>
                                <option value="popular">Most popular</option>
                                <option value="price-low">Price low</option>
                                <option value="price-high">Price high</option>
                                <option value="title">Title A-Z</option>
                                <option value="oldest">Oldest</option>
                            </select>
                            <FiChevronDown />
                        </label>
                        <button
                            type="button"
                            className={`market-filter-toggle ${showPriceFilter ? "active" : ""}`}
                            onClick={() => setShowPriceFilter((current) => !current)}
                        >
                            <FiSliders />
                            Price
                        </button>
                    </div>
                </section>

                {showPriceFilter && (
                    <section className="market-price-panel" aria-label="Price range">
                        <label>
                            Min
                            <input
                                type="number"
                                min="0"
                                max="1000"
                                value={priceRange.min}
                                onChange={(event) => setPriceRange({ ...priceRange, min: Number(event.target.value) })}
                            />
                        </label>
                        <label>
                            Max
                            <input
                                type="number"
                                min="0"
                                max="1000"
                                value={priceRange.max}
                                onChange={(event) => setPriceRange({ ...priceRange, max: Number(event.target.value) })}
                            />
                        </label>
                        <button type="button" onClick={() => setPriceRange({ min: 0, max: 1000 })}>
                            Reset range
                        </button>
                    </section>
                )}

                {selectedArtist && (
                    <div className="market-active-filter">
                        <span>Showing beats by {selectedArtist}</span>
                        <button type="button" onClick={clearArtistFilter}>Clear artist</button>
                    </div>
                )}

                {loading && (
                    <div className="market-state">
                        <div className="spinner"></div>
                        <p>Loading beats...</p>
                    </div>
                )}

                {!loading && error && (
                    <div className="market-state error">
                        <FiFilter />
                        <h2>Something interrupted the live catalog.</h2>
                        <p>{error}</p>
                    </div>
                )}

                {!loading && !error && (
                    <>
                        {featuredTracks.length > 0 && (
                            <section className="market-section">
                                <div className="market-section-header">
                                    <div>
                                        <span>Curated for launch</span>
                                        <h2>Featured beats</h2>
                                    </div>
                                    <strong>{featuredTracks.length} picks</strong>
                                </div>
                                <div className="market-featured-grid">
                                    {featuredTracks.map((track) => renderCard(track, true))}
                                </div>
                            </section>
                        )}

                        <section className="market-section">
                            <div className="market-section-header">
                                <div>
                                    <span>Full catalog</span>
                                    <h2>All beats</h2>
                                </div>
                                <strong>{filteredTracks.length.toLocaleString()} results</strong>
                            </div>

                            {filteredTracks.length === 0 ? (
                                <div className="market-state">
                                    <FiMusic />
                                    <h2>No beats found</h2>
                                    <p>Try a different search, genre, or price range.</p>
                                </div>
                            ) : (
                                <div className="market-track-grid">
                                    {filteredTracks.map((track) => renderCard(track))}
                                </div>
                            )}
                        </section>

                        {recentlyViewed.length > 0 && (
                            <section className="market-section recent">
                                <div className="market-section-header">
                                    <div>
                                        <span>Listening history</span>
                                        <h2>Recently viewed</h2>
                                    </div>
                                    <button
                                        type="button"
                                        className="market-clear-button"
                                        onClick={() => {
                                            setRecentlyViewed([]);
                                            localStorage.removeItem("recentlyViewed");
                                        }}
                                    >
                                        Clear
                                    </button>
                                </div>
                                <div className="market-track-grid compact">
                                    {recentlyViewed.slice(0, 4).map((track) => renderCard(track))}
                                </div>
                            </section>
                        )}
                    </>
                )}
            </main>

            {playingTrack?.fileUrl && (
                <div className="market-player" role="region" aria-label="Audio preview player">
                    <audio
                        ref={audioRef}
                        src={playingTrack.fileUrl}
                        onTimeUpdate={handleAudioTimeUpdate}
                        onLoadedMetadata={(event) => setPlayerDuration(event.target.duration || 0)}
                        onEnded={() => {
                            setPlayingTrack(null);
                            setPlayerTime(0);
                        }}
                    />
                    <div className="market-player-track">
                        <img src={playingTrack.coverImage} alt="" />
                        <span>
                            <strong>{playingTrack.title || "Untitled beat"}</strong>
                            <small>{playingTrack.artist || "Unknown producer"}</small>
                        </span>
                    </div>
                    <button type="button" className="market-player-play" onClick={(event) => handlePlayPreview(playingTrack, event)}>
                        <FiPause />
                    </button>
                    <div className="market-player-progress">
                        <span>{formatTime(playerTime)}</span>
                        <input
                            type="range"
                            min="0"
                            max={Math.min(playerDuration || 30, 30)}
                            value={Math.min(playerTime, 30)}
                            onChange={handleSeek}
                            aria-label="Preview position"
                        />
                        <span>{formatTime(Math.min(playerDuration || 30, 30))}</span>
                    </div>
                    <button
                        type="button"
                        className="market-player-license"
                        onClick={() => handleCheckout(playingTrack, "preview_player")}
                    >
                        License {formatPrice(playingTrack.price)}
                    </button>
                </div>
            )}

            {checkoutTrack && (
                <Checkout
                    track={checkoutTrack}
                    source={checkoutSource}
                    onSuccess={() => {
                        setCheckoutTrack(null);
                    }}
                    onCancel={() => setCheckoutTrack(null)}
                />
            )}
        </div>
    );
};

export default Marketplace;
